import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ListItem, List } from '@mui/material';
import { isNil } from 'lodash-es';

import { BodySmall, Box, Modal, ModalFooter, ModalHeader, StatusSelect, TextField, Tooltip, SelectChangeEvent, OptionType, Autocomplete, Skeleton } from '@parspec/pixel';

import LotSelect from '../../lotSelect';

import { priceRelatedCols, LEAD_TIME_UNITS } from '../../shared/constants';
import { updateMsg } from 'src/features/shared/constants';

import { getUpdatedPricingInfoForChange } from '../../shared/utils';
import { convertToCents, searchManufacturer } from '../../../../shared/utils/utils';
import { useNinetyViewportHeight } from '../../../shared/hooks';

import { BulkEditPayload } from './queries/apiTypes';
import { useBulkEditPatchMutation } from './queries';
import OptionsDropDown from '../../../shared/OptionsDropDown';
import {
    BULKEDIT_POPUP_TEXT,
    BULK_EDIT_ASSIGN_LOT_WARNING,
    BULK_EDIT_COST_WARNING,
    BULK_EDIT_KIT_WARNING,
    BULK_EDIT_LOT_WARNING,
    BULK_EDIT_OPTION_WARNING,
    BULK_EDIT_RESET_LOT_WARNING,
    COMPONENTS_QTY_INFO_TEXT,
    LOT_SELECTED_TOOLTIP_TEXT,
    PRODUCT_QTY_INFO_TEXT
} from '../constants';
import { Template as LeadTimeUnitTemplate, options as leadTimeUnitOptions } from '../../shared/LeadTimeUnitTemplate';
import { getFormattedLeadTimeRange, getFormattedManufacturerName, getValidatedLeadTimeRange } from 'src/features/BOM/shared/utils';
import { SPECIFICATION_VALUES, SPECIFICATION_OPTIONS, BOM_TABLES_ENUM } from '../../../shared/constants';
import useGetSelectedDataInfo from 'src/features/BOM/shared/hooks/useGetSelectedDataInfoHook';
import { ManufacturerDropdownType } from 'src/features/BOM/productFinder/setRequirements/types';
import { useCompanyMfgQuery, } from 'src/features/Settings/Manufacturer/queries';
import { isValidCharactersForMfg } from 'src/features/Settings/Manufacturer/utils';
import { INVALID_CHARACTERS_IN_MFG_NAME } from 'src/features/Settings/Manufacturer/constants';
import { OperationType, RoundingBehaviour } from 'src/features/BOM/shared/types';

export type EditOptionsT = 'qty' | 'qtyPer' | 'manufacturer' | 'specification' | 'lot' | 'productToReplace' | 'cost' | 'discount' | 'discountedCost' | 'margin' | 'sellPrice' | 'leadTimeUnit' | 'leadTimeValue' | 'quoteNo' | 'externalNotes' | 'internalComments'

interface IBulkEditProps {
    open: boolean;
    onClose: () => void;
    isOptionsBulkEdit?: boolean;
    selectedItems: Array<any>;
    handleClearSelection: () => void;
    setSnackbarOpen: (message: string, autoHideDuration?: number | null) => void;
    onReset?: () => void;
    lotMap?: Record<number, number | null>;
    rounding: RoundingBehaviour;
    roundingFieldName: string;
    options: EditOptionsT[];
    updateKitCostCallback: (type: OperationType, bodId?: number, payload?: BulkEditPayload[]) => Promise<void>
}

type IBulkEditTableDataType = {
    qty: string | null;
    qtyPer: string | null;
    manufacturer: string | null;
    specification: SPECIFICATION_VALUES | '';
    lotId: string;
    product_to_replace: string | null;
    cost: number | string | null;
    discount: string | null;
    discountedCost: string | null;
    extendedDiscountCost: number | null;
    margin: string | null;
    sellPrice: string | null;
    leadTimeUnit: string | null;
    leadTimeValue: string | null;
    quoteNo: string | null;
    externalNotes: string | null;
    internalComments: string | null;
};

const isOnlyLeadTimeMinPresent = (
    { leadTimeMin, leadTimeMax }: { leadTimeMin: number | null; leadTimeMax: number | null; }
) => !isNil(leadTimeMin) && isNil(leadTimeMax);

const BulkEditModal = ({
    open,
    onClose,
    selectedItems,
    handleClearSelection,
    isOptionsBulkEdit: optionsEdit = false,
    setSnackbarOpen,
    onReset,
    lotMap = {} as Record<number, number | null>,
    rounding,
    roundingFieldName,
    options,
    updateKitCostCallback
}: IBulkEditProps) => {
    const { bomId } = useParams();
    const { data: companyMfgData, isLoading: isMfgListLoading } = useCompanyMfgQuery();
    const [formValues, updateFormValue] = useState<IBulkEditTableDataType>({
        qty: null,
        qtyPer: null,
        manufacturer: null,
        lotId: '',
        specification: '',
        product_to_replace: null,
        cost: null,
        discount: null,
        discountedCost: null,
        extendedDiscountCost: null,
        margin: null,
        sellPrice: null,
        leadTimeUnit: null,
        // leadTimeValue string field is only needed for form input. It will be split
        // and saved into leadTimeMin and leadTimeMax fields in BODs
        leadTimeValue: null,
        quoteNo: null,
        externalNotes: null,
        internalComments: null
    });
    const [selectedBods, setSelectedBodsInfo] = useState(selectedItems);
    const [nullCostFound, setNullCostFound] = useState(false);
    const [lotExists, setLotExists] = useState(false);
    const [lotSelected, setLotSelected] = useState(false);
    const [resetLotWarning, setResetLotWarning] = useState(false);
    const [hasOption, setHasOption] = useState(false);
    const [isPartOfKit, setIsPartOfKit] = useState(false);
    const [isLeadTimeValueDisabled, setIsLeadTimeValueDisabled] = useState(false);

    const { mutateAsync: patchBulkEdit, isLoading } = useBulkEditPatchMutation();

    const { componentIds, productIds, completelySelectedKitIdsFromPartialSections } = useGetSelectedDataInfo({ selectedItems })

    const enableQty = useMemo(() => completelySelectedKitIdsFromPartialSections.length > 0 || productIds.length > 0, [completelySelectedKitIdsFromPartialSections, productIds])

    const enableQtyPer = useMemo(() => componentIds.length > 0, [componentIds])

    const modalMaxHeight = useNinetyViewportHeight();
    /** here we are subtracting sum of heights of header and footer(120) from total modal height to fetch content height */
    const boxMaxHeight = modalMaxHeight - 120;
    /** here we are subtracting height of text used above form in content for get only form's height */
    const formHeight = boxMaxHeight - 64;
    const handleModalSave = async () => {
        const payload: Array<BulkEditPayload> = [];
        selectedBods.forEach((item: any) => {
            const {
                id,
                section,
                qty,
                qtyPer,
                manufacturer,
                lotId,
                cost,
                discount,
                discountedCost,
                extendedDiscountCost,
                margin,
                sellPrice,
                leadTimeUnit,
                leadTimeMin,
                leadTimeMax,
                quoteNo,
                product_to_replace,
                externalNotes,
                specification,
                internalComments,
                substitute_section,
                per,
                groupPath
            } = item;

            if (groupPath.length === 1) return;

            let obj: BulkEditPayload = {
                bod_data: {
                    bod_id: id,
                    qty: groupPath.length === 3 ? parseToNumber(String(qtyPer)) : parseToNumber(String(qty)),
                    manufacturer: trimAndConvertToUpperCase(manufacturer),
                    specification: specification
                },
                metadata: {
                    cost_cents: cost !== null && String(cost).length > 0 ? convertToCents(cost) : null,
                    discount_percentage: discount,
                    discounted_cost_cents: discountedCost !== null && String(discountedCost).length > 0 ? convertToCents(discountedCost) : null,
                    extended_discounted_cost_cents: extendedDiscountCost !== null && String(extendedDiscountCost).length > 0 ? convertToCents(extendedDiscountCost) : null,
                    margin_percentage: margin,
                    sell_price_cents: sellPrice !== null && String(sellPrice).length > 0 ? convertToCents(sellPrice) : null,
                    lead_time_unit: leadTimeUnit,
                    lead_time_min: leadTimeMin ?? null,
                    lead_time_max: leadTimeMax ?? null,
                    source_quote_number: trimAndConvertToUpperCase(quoteNo),
                    notes: trimAndConvertToUpperCase(externalNotes || ''),
                    internal_notes: trimAndConvertToUpperCase(internalComments || ''),
                    per
                }
            };

            if ((obj.metadata.lead_time_unit === null || obj.metadata.lead_time_unit === '-') && !isNil(obj.metadata.lead_time_min)) {
                obj.metadata.lead_time_unit = LEAD_TIME_UNITS.WEEKS.toLowerCase();
            }

            if (optionsEdit) {
                obj = { ...obj, bod_data: { ...obj.bod_data, substitute_section_id: substitute_section, product_to_replace: product_to_replace } };
            } else {
                obj = { ...obj, bod_data: { ...obj.bod_data, section_id: section }, metadata: { ...obj.metadata, lot_id: lotId !== '' ? lotId : null } };
            }

            payload.push(obj);
        });

        await patchBulkEdit({ bomId: Number(bomId), payload });
        updateKitCostCallback('bulk-edit', undefined, payload);
        onReset && onReset();
        handleClearSelection();
        setSnackbarOpen(updateMsg);
        onClose();
    };

    const trimAndConvertToUpperCase = (str: string) => {
        if (!str) return null;

        return str.length > 0 ? str.trim().toUpperCase() : null;
    };

    const parseToNumber = (str: string) => {
        if (!str) return null;

        return String(str).length > 0 ? Number(str) : null;
    };

    const areAllPricingFieldEmpty = (name: string, value: string) => {
        const { cost, discount, discountedCost, margin, sellPrice } = { ...formValues, [name]: value } as IBulkEditTableDataType;
        return !cost && !discount && !discountedCost && !margin && !sellPrice;
    };

    const handleLeadRangeValidation = () => {
        const response = getValidatedLeadTimeRange(formValues.leadTimeValue ?? "");
        const leadTimeUnitFormValue = formValues.leadTimeUnit;

        if (response.isError) {
            // In case of error reset the leadTimeValue field
            updateFormValue(prev => ({ ...prev, leadTimeValue: null }));
            setSelectedBodsInfo(prevBods => prevBods.map(bod => ({
                ...bod,
                leadTimeMin: null,
                leadTimeMax: null,
            })));
            setSnackbarOpen(response.message, 5000);
            return;
        }

        // Range is valid, so set the state correctly for form and bods
        const [leadTimeMin, leadTimeMax] = response.tuple;
        const newLeadTimeValue = getFormattedLeadTimeRange({ leadTimeMin, leadTimeMax });

        updateFormValue(prev => ({
            ...prev,
            leadTimeValue: newLeadTimeValue,
            // If non empty leadTimeValue is added, set unit to days(if prev unit is null)
            leadTimeUnit: prev.leadTimeUnit
                ?? (newLeadTimeValue ? LEAD_TIME_UNITS.WEEKS.toLowerCase() : null),
        }));

        setSelectedBodsInfo(prevBods => prevBods.map(bod => ({
            ...bod,
            leadTimeMin,
            // In case user only set leadTimeMin value, empty leadTimeMax by passing -1 flag
            leadTimeMax: isOnlyLeadTimeMinPresent({ leadTimeMin, leadTimeMax })
                ? -1 : leadTimeMax,
            // Keep the already selected lead time Unit(if it is not null)
            // Else: set the value to days, if valid leadTimeValue is added. 
            leadTimeUnit: leadTimeUnitFormValue
                ?? (newLeadTimeValue ? LEAD_TIME_UNITS.WEEKS.toLowerCase() : bod.leadTimeUnit)
        })));

        // show message if adjusted value
        if (response.message) setSnackbarOpen(response.message, 5000);
    }

    const handleformChange = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown> | React.SyntheticEvent<Element, Event>) => {
            const target = event.target as HTMLInputElement;
            let value = target?.value;
            const name = target?.name;
            if (priceRelatedCols.has(name as BOM_TABLES_ENUM)) {
                const newValue = value.replace(/[^0-9.]/g, '');
                value = newValue.replace(/(\..*)\./g, '$1');
            } else if (name === BOM_TABLES_ENUM.QTY || name === BOM_TABLES_ENUM.QTY_PER) {
                value = value.replace(/[^0-9]/g, '');
            }

            if (name === BOM_TABLES_ENUM.LOT && value !== '') {
                setNullCostFound(false);
                setLotExists(false);
                updateFormValue(prevFormValues => ({
                    ...prevFormValues,
                    [name]: value,
                    [BOM_TABLES_ENUM.COST]: null,
                    [BOM_TABLES_ENUM.DISCOUNT]: null,
                    [BOM_TABLES_ENUM.DISCOUNT_COST]: null,
                    [BOM_TABLES_ENUM.EXT_DISCOUNT_COST]: null,
                    [BOM_TABLES_ENUM.MARGIN]: null,
                    [BOM_TABLES_ENUM.SELL_PRICE]: null
                }));
            } else {
                updateFormValue(prevFormValues => ({ ...prevFormValues, [name]: value }));
            }

            if (name === BOM_TABLES_ENUM.COST) {
                if (value?.length > 0) setNullCostFound(false);
            }

            // Instead of setting internal component state in the following selectedBods loop
            // Just set it once outside here
            if (name === BOM_TABLES_ENUM.LEAD_TIME_UNIT) {
                if (value === '-') {
                    updateFormValue(prevFormValues => ({
                        ...prevFormValues,
                        leadTimeUnit: null,
                        leadTimeValue: null,
                    }));
                }

                if (value === LEAD_TIME_UNITS.IN_STOCK.toLowerCase()) {
                    updateFormValue(prevFormValues => ({
                        ...prevFormValues,
                        leadTimeValue: null,
                        leadTimeUnit: value,
                    }));
                    setIsLeadTimeValueDisabled(true);
                } else {
                    setIsLeadTimeValueDisabled(false);
                }
            }

            const {
                qty: formQty,
                qtyPer: formQtyPer,
                manufacturer,
                lotId,
                product_to_replace,
                leadTimeUnit,
                leadTimeValue,
                quoteNo,
                externalNotes
            } = formValues;
            // Although min and max will always be valid here, but just in case
            // they are not, the following function will handle it.
            const response = getValidatedLeadTimeRange(leadTimeValue ?? "");
            const [leadTimeMin, leadTimeMax] = response.isError
                ? [null, null] : response.tuple;

            if (priceRelatedCols.has(name as BOM_TABLES_ENUM)) {
                const updatedValues = selectedBods.map((item: any, index: number) => {
                    if (selectedItems?.[index].lotId && !areAllPricingFieldEmpty(name, value) && Number(lotId) !== 0) {
                        setLotExists(true);
                    } else if (!value && value !== '0') setLotExists(false);

                    if (selectedItems?.[index].is_kit && !areAllPricingFieldEmpty(name, value)) {
                        setIsPartOfKit(true);
                    } else if (!value && value !== '0') setIsPartOfKit(false);

                    if (selectedItems?.[index].lotId || selectedItems?.[index].is_kit) {
                        return {
                            ...selectedItems[index],
                            qty: selectedItems?.[index].groupPath.length === 3 ? (formQtyPer ? formQtyPer : selectedItems[index].qty) : (formQty ? formQty : selectedItems[index].qty),
                            manufacturer: manufacturer ? manufacturer : selectedItems[index].manufacturer,
                            lotId: lotId !== '' ? lotId : selectedItems[index].lotId,
                            product_to_replace: product_to_replace ? product_to_replace : selectedItems[index].product_to_replace,
                            leadTimeUnit: leadTimeUnit ? leadTimeUnit : selectedItems[index].leadTimeUnit,
                            leadTimeMin: leadTimeMin ?? selectedItems[index].leadTimeMin,
                            leadTimeMax: leadTimeMax ?? selectedItems[index].leadTimeMax,
                            quoteNo: quoteNo ? quoteNo : selectedItems[index].quoteNo,
                            externalNotes: externalNotes ? externalNotes : selectedItems[index].externalNotes,
                            internalComments: internalComments ? internalComments : selectedItems[index].internalComments,
                            specification: specification ? specification : selectedItems[index].specification
                        };
                    }

                    if (name !== BOM_TABLES_ENUM.COST && (item.cost === null || item.cost == 0) && !areAllPricingFieldEmpty(name, value)) {
                        setNullCostFound(true);
                        return { ...item };
                    } else if (name !== BOM_TABLES_ENUM.COST && !value.length && value !== '0') setNullCostFound(false);

                    item = { ...item, [name]: value.length === 0 ? selectedItems?.[index][name] : value };

                    if (
                        name === BOM_TABLES_ENUM.COST &&
                        (item.cost === null || item.cost == 0) &&
                        (formValues.discount?.length || formValues.discountedCost?.length || formValues.margin?.length || formValues.sellPrice?.length)
                    ) {
                        setNullCostFound(true);
                    }

                    const { cost, discount, discountedCost, extendedDiscountCost, margin, sellPrice, qty } = item;

                    const priceInfo = {
                        cost: parseToNumber(cost),
                        discount: parseToNumber(discount),
                        discountedCost: parseToNumber(discountedCost),
                        extendedDiscountCost: parseToNumber(extendedDiscountCost),
                        margin: parseToNumber(margin),
                        sellPrice: parseToNumber(sellPrice),
                        qty: parseToNumber(qty),
                        extendedPrice: Number(qty) * Number(sellPrice),
                        roundingPrecision: rounding,
                        roundingBehaviourOnCell: roundingFieldName
                    };

                    const pricingRowInfoToUpdate = getUpdatedPricingInfoForChange(priceInfo, name as BOM_TABLES_ENUM);
                    return { ...item, ...pricingRowInfoToUpdate };
                });

                setSelectedBodsInfo(updatedValues);
            } else {
                const updatedValues = selectedBods.map((item: any, index: number) => {
                    if (name === BOM_TABLES_ENUM.LOT) {
                        if (Number(value) !== 0 && value !== '' && selectedItems?.[index].cost !== null) setLotSelected(true);
                        else if (Number(value) === 0 || value === '') {
                            setLotSelected(false);
                            setLotExists(false);
                        }

                        if (Number(value) === 0 && selectedItems?.[index].lotId) {
                            setResetLotWarning(true);
                        }

                        if (Number(value) !== 0 && value !== '' && item.hasOptions) {
                            setLotSelected(false);
                            setHasOption(true);
                            if (selectedBods.length === 1) setLotSelected(false);
                            return {
                                ...selectedItems?.[index],
                                qty: selectedItems?.[index].groupPath.length === 3 ? (formQtyPer ? formQtyPer : selectedItems[index].qty) : (formQty ? formQty : selectedItems[index].qty),
                                manufacturer: manufacturer ? manufacturer : selectedItems[index].manufacturer,
                                lotId: lotId !== '' ? lotId : selectedItems[index].lotId,
                                product_to_replace: product_to_replace ? product_to_replace : selectedItems[index].product_to_replace,
                                leadTimeUnit: leadTimeUnit ? leadTimeUnit : selectedItems[index].leadTimeUnit,
                                leadTimeMin: leadTimeMin ?? selectedItems[index].leadTimeMin,
                                leadTimeMax: leadTimeMax ?? selectedItems[index].leadTimeMax,
                                quoteNo: quoteNo ? quoteNo : selectedItems[index].quoteNo,
                                externalNotes: externalNotes ? externalNotes : selectedItems[index].externalNotes,
                                internalComments: internalComments ? internalComments : selectedItems[index].internalComments,
                                specification: specification ? specification : selectedItems[index].specification
                            };
                        } else {
                            setHasOption(false);
                        }
                    }

                    if (name === BOM_TABLES_ENUM.LEAD_TIME_UNIT) {
                        if (value === '-') {
                            item = {
                                ...item,
                                leadTimeUnit: null,
                                leadTimeMin: null,
                                leadTimeMax: null,
                            };
                            return item;
                        }

                        if (value === LEAD_TIME_UNITS.IN_STOCK.toLowerCase()) {
                            item = {
                                ...item,
                                leadTimeMin: -1,
                                leadTimeMax: -1,
                            };
                        }
                    }

                    if (name === BOM_TABLES_ENUM.LEAD_TIME_VALUE) {
                        // We don't have to update the min & max lead time fields in bod yet
                        // They'll be validated and updated on onBlur
                        return item;
                    }

                    return name === BOM_TABLES_ENUM.LOT
                        ? value !== ''
                            ? {
                                ...item,
                                [name]: typeof value === 'string' ? value.trim() : value,
                                [BOM_TABLES_ENUM.COST]: null,
                                [BOM_TABLES_ENUM.DISCOUNT]: null,
                                [BOM_TABLES_ENUM.DISCOUNT_COST]: null,
                                [BOM_TABLES_ENUM.MARGIN]: null,
                                [BOM_TABLES_ENUM.SELL_PRICE]: null
                            }
                            : {
                                ...item,
                                [name]: null,
                                [BOM_TABLES_ENUM.COST]: selectedItems[index].cost,
                                [BOM_TABLES_ENUM.DISCOUNT]: selectedItems[index].discount,
                                [BOM_TABLES_ENUM.DISCOUNT_COST]: selectedItems[index].discountedCost,
                                [BOM_TABLES_ENUM.MARGIN]: selectedItems[index].margin,
                                [BOM_TABLES_ENUM.SELL_PRICE]: selectedItems[index].sellPrice
                            }
                        : { ...item, [name]: typeof value === 'string' ? value.trim() : value };
                });
                setSelectedBodsInfo(updatedValues);
            }
        },
        [formValues]
    );

    const { qty, qtyPer, manufacturer, lotId, product_to_replace, cost, discount, discountedCost, margin, sellPrice, leadTimeUnit, leadTimeValue, quoteNo, externalNotes, specification, internalComments } =
        formValues;

    const filterHandler = (options: OptionType<ManufacturerDropdownType>[] | { [index: string]: string | number; }[], params: { inputValue: string }) => {
        return searchManufacturer(options as OptionType<ManufacturerDropdownType>[], params.inputValue.trim());
    };
    const handleMfgNameBlur = () => {
        const value = getFormattedManufacturerName(companyMfgData, formValues.manufacturer as string)

        if (value) {
            const { isValid, sanitizedValue } = isValidCharactersForMfg(value || '');
            const manufacturerValue = isValid ? (value || '').trim() : sanitizedValue;
            if (!isValid) {
                setSnackbarOpen(INVALID_CHARACTERS_IN_MFG_NAME);
            }

            updateFormValue({
                ...formValues, manufacturer: manufacturerValue
            });
            setSelectedBodsInfo((prevBods) =>
                prevBods.map((bod) => ({
                    ...bod,
                    manufacturer: manufacturerValue,
                }))
            );
        }
    };
    const handleMfgNameChange = (event: any) => {
        if (event.target.value) {
            const newManufacturer = event.target.value?.manufacturer_name || '';
            // Update the form values
            updateFormValue({
                ...formValues,
                manufacturer: newManufacturer,
            });

            // Update the manufacturer field for all objects in the array
            setSelectedBodsInfo((prevBods) =>
                prevBods.map((bod) => ({
                    ...bod,
                    manufacturer: newManufacturer,
                }))
            );
        }
    };

    const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
        const value = getFormattedManufacturerName(companyMfgData, newInputValue as string)

        if (value) {
            const newManufacturer = value || '';
            // Update the form values
            updateFormValue({
                ...formValues,
                manufacturer: newInputValue,
            });

            // Update the manufacturer field for all objects in the array
            setSelectedBodsInfo((prevBods) =>
                prevBods.map((bod) => ({
                    ...bod,
                    manufacturer: newManufacturer,
                }))
            );
        }
    }

    return (
        <Modal
            open={open}
            header={<ModalHeader title="Bulk Edit" onClose={onClose} />}
            footer={<ModalFooter onAccept={handleModalSave} onReject={onClose} continueButtonLabel="Save" isLoading={isLoading} />}
        >
            <Box width={500} maxHeight={boxMaxHeight}>
                <BodySmall limit={false} pb={3} color={'text.secondary'} sx={{ lineHeight: '20px' }}>
                    {BULKEDIT_POPUP_TEXT}
                </BodySmall>
                <Box maxHeight={formHeight} overflow="auto">
                    {(nullCostFound || lotExists || lotSelected || hasOption || isPartOfKit || resetLotWarning) && (
                        <List sx={{ listStyleType: 'disc', pl: 5 }}>
                            <BodySmall color={'warning.main'} limit={false} pb={2}>
                                {nullCostFound && (
                                    <ListItem sx={{ display: 'list-item' }} disablePadding>
                                        {BULK_EDIT_COST_WARNING}
                                    </ListItem>
                                )}
                                {lotExists && (
                                    <ListItem sx={{ display: 'list-item' }} disablePadding>
                                        {BULK_EDIT_LOT_WARNING}
                                    </ListItem>
                                )}
                                {lotSelected && (
                                    <ListItem sx={{ display: 'list-item' }} disablePadding>
                                        {BULK_EDIT_ASSIGN_LOT_WARNING}
                                    </ListItem>
                                )}
                                {hasOption && (
                                    <ListItem sx={{ display: 'list-item' }} disablePadding>
                                        {BULK_EDIT_OPTION_WARNING}
                                    </ListItem>
                                )}
                                {isPartOfKit && (
                                    <ListItem sx={{ display: 'list-item' }} disablePadding>
                                        {BULK_EDIT_KIT_WARNING}
                                    </ListItem>
                                )}
                                {resetLotWarning && (
                                    <ListItem sx={{ display: 'list-item' }} disablePadding>
                                        {BULK_EDIT_RESET_LOT_WARNING}
                                    </ListItem>
                                )}
                            </BodySmall>
                        </List>
                    )}
                    {options.includes('qty') && <Box display="flex" alignItems='center' gap={2} pt={2} >
                        <Box width="20%"><TextField value={qty || ''} name={BOM_TABLES_ENUM.QTY} onChange={handleformChange} label="Qty" inputProps={{ maxLength: 10 }} disabled={!enableQty} /></Box>
                        <Box width="80%">
                            <BodySmall limit={false} color={'text.secondary'} sx={{ lineHeight: '20px' }}>
                                {PRODUCT_QTY_INFO_TEXT}
                            </BodySmall>
                        </Box>
                    </Box>}
                    {options.includes('qtyPer') && <Box display="flex" alignItems='center' gap={2} pt={4} >
                        <Box width="20%"><TextField value={qtyPer || ''} name={BOM_TABLES_ENUM.QTY_PER} onChange={handleformChange} label="Qty per" inputProps={{ maxLength: 10 }} disabled={!enableQtyPer} /></Box>
                        <Box width="80%">
                            <BodySmall limit={false} color={'text.secondary'} sx={{ lineHeight: '20px' }}>
                                {COMPONENTS_QTY_INFO_TEXT}
                            </BodySmall>
                        </Box>
                    </Box>}
                    {options.includes('manufacturer') && <Box display="flex" pt={4}>
                        <Box width="300px">
                            {!isMfgListLoading ? (
                                <Autocomplete
                                    value={manufacturer || ''}
                                    options={companyMfgData?.data as any}
                                    onBlur={handleMfgNameBlur}
                                    onChange={handleMfgNameChange}
                                    onTextFieldChange={handleInputChange}
                                    placeholder="Type a manufacturer..."
                                    freeSolo={true}
                                    label="Manufacturer Name"
                                    optionlabelkeyname="manufacturer_name"
                                    filterOptionsCallBack={filterHandler}
                                />
                            ) : (
                                <Skeleton variant="rectangular" width="100%" height={36} sx={{ borderRadius: 1 }} />
                            )}
                        </Box>
                    </Box>}
                    {options.includes('specification') && <Box display="flex" pt={4}>
                        <Box width="300px">
                            <StatusSelect
                                options={SPECIFICATION_OPTIONS}
                                value={specification}
                                name="specification"
                                onChange={handleformChange}
                                size="small"
                                label="Specification"
                                selectedOptionFontSize="14px"
                            />
                        </Box>
                    </Box>}
                    <Box display="flex" pt={4}>
                        {optionsEdit ?
                            (options.includes('productToReplace') && <Box width="300px">
                                <OptionsDropDown lotMap={lotMap} bomId={bomId || ''} name="product_to_replace" value={product_to_replace || ''} onChange={handleformChange} />
                            </Box>
                            ) : (
                                options.includes('lot') && <Box width="200px">
                                    <LotSelect size='small' value={lotId} fieldName={BOM_TABLES_ENUM.LOT} onChange={handleformChange} isBulkEdit={true} />
                                </Box>
                            )}
                    </Box>
                    {options.includes('cost') && <Box display="flex" pt={4}>
                        <Tooltip title={lotId !== '0' && lotId !== '' ? <p style={{ whiteSpace: 'pre-line' }}>{LOT_SELECTED_TOOLTIP_TEXT}</p> : ''} placement="top">
                            <Box width="120px">
                                <TextField
                                    value={cost || ''}
                                    name={BOM_TABLES_ENUM.COST}
                                    placeholder="Cost"
                                    onChange={handleformChange}
                                    disabled={Number(lotId) !== 0 && lotId !== ''}
                                    label="Cost"
                                    startIcon="$"
                                />
                            </Box>
                        </Tooltip>
                    </Box>}
                    <Box display="flex" alignItems="center" justifyContent="space-between" pt={4} width="330px">
                        {options.includes('discount') && <Tooltip title={lotId !== '0' && lotId !== '' ? <p style={{ whiteSpace: 'pre-line' }}>{LOT_SELECTED_TOOLTIP_TEXT}</p> : ''} placement="top">
                            <Box width="152px">
                                <TextField
                                    value={discount || ''}
                                    name={BOM_TABLES_ENUM.DISCOUNT}
                                    onChange={handleformChange}
                                    label="Discount (%)"
                                    disabled={(discountedCost && discountedCost.length > 0) || (Number(lotId) !== 0 && lotId !== '')}
                                />
                            </Box>
                        </Tooltip>}
                        {options.includes('discount') && <BodySmall>or</BodySmall>}
                        {options.includes('discountedCost') && <Tooltip title={lotId !== '0' && lotId !== '' ? <p style={{ whiteSpace: 'pre-line' }}>{LOT_SELECTED_TOOLTIP_TEXT}</p> : ''} placement="top">
                            <Box width="152px">
                                <TextField
                                    value={discountedCost || ''}
                                    name={BOM_TABLES_ENUM.DISCOUNT_COST}
                                    onChange={handleformChange}
                                    label="Discounted Cost"
                                    placeholder="Discounted Cost"
                                    disabled={(discount && discount.length > 0) || (Number(lotId) !== 0 && lotId !== '')}
                                    startIcon="$"
                                />
                            </Box>
                        </Tooltip>}
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" pt={4} width="330px">
                        {options.includes('margin') && <Tooltip title={lotId !== '0' && lotId !== '' ? <p style={{ whiteSpace: 'pre-line' }}>{LOT_SELECTED_TOOLTIP_TEXT}</p> : ''} placement="top">
                            <Box width="152px">
                                <TextField
                                    value={margin || ''}
                                    name={BOM_TABLES_ENUM.MARGIN}
                                    onChange={handleformChange}
                                    label="Margin (%)"
                                    disabled={(sellPrice && sellPrice?.length > 0) || (Number(lotId) !== 0 && lotId !== '')}
                                />
                            </Box>
                        </Tooltip>}
                        {options.includes('margin') && <BodySmall>or</BodySmall>}
                        {options.includes('sellPrice') && <Tooltip title={lotId !== '0' && lotId !== '' ? <p style={{ whiteSpace: 'pre-line' }}>{LOT_SELECTED_TOOLTIP_TEXT}</p> : ''} placement="top">
                            <Box width="152px">
                                <TextField
                                    value={sellPrice || ''}
                                    name={BOM_TABLES_ENUM.SELL_PRICE}
                                    onChange={handleformChange}
                                    label="Sell Price"
                                    placeholder="Sell Price"
                                    disabled={(margin && margin.length > 0) || (Number(lotId) !== 0 && lotId !== '')}
                                    startIcon="$"
                                />
                            </Box>
                        </Tooltip>}
                    </Box>
                    {options.includes('leadTimeValue') && <Box display="flex" pt={4}>
                        <Box width="200px">
                            <TextField
                                disabled={isLeadTimeValueDisabled}
                                value={leadTimeValue || ''}
                                name={BOM_TABLES_ENUM.LEAD_TIME_VALUE}
                                onBlur={handleLeadRangeValidation}
                                onChange={handleformChange}
                                label="Lead Time Value"
                            />
                        </Box>
                    </Box>}
                    {options.includes('leadTimeUnit') && <Box display="flex" pt={4}>
                        <Box width="200px">
                            <LeadTimeUnitTemplate
                                showLabel={true}
                                fieldName={BOM_TABLES_ENUM.LEAD_TIME_UNIT}
                                handleOnChange={handleformChange}
                                leadTimeUnit={leadTimeUnit || '-'}
                                options={leadTimeUnitOptions}
                                size='small'
                            />
                        </Box>
                    </Box>}

                    {options.includes('quoteNo') && <Box display="flex" pt={4}>
                        <Box width="200px">
                            <TextField value={quoteNo || ''} name={BOM_TABLES_ENUM.QUOTE_NO} onChange={handleformChange} label="Source Quote Number" />
                        </Box>
                    </Box>}
                    {options.includes('externalNotes') && <Box display="flex" pt={4}>
                        <Box width="100%">
                            <TextField multiline rows={5} name="externalNotes" value={externalNotes || ''} onChange={handleformChange} label="Quote Notes(External)" />
                        </Box>
                    </Box>}
                    {options.includes('internalComments') && <Box display="flex" pt={4}>
                        <Box width="100%">
                            <TextField multiline rows={5} name="internalComments" value={internalComments || ''} onChange={handleformChange} label="Internal Comments" />
                        </Box>
                    </Box>}
                </Box>
            </Box>
        </Modal>
    );
};

export default BulkEditModal;
